import { legibleLines } from '../legibleLines';
import { rankLabelLines } from '../rankLabelLines';
import { toLeafText, type MeasureLabel, type WrapLabel } from './toLeafText';

type ToTileTextArgs = {
  label: string | null;
  /** Already formatted and parenthesised, e.g. `(4.7M)`. */
  valueLabel: string;
  /** The tile's inner width, padding already taken off but not the icon. */
  availableWidth: number;
  availableHeight: number;
  /** What the icon and its gap would cost, or `0` where the tile has no icon to draw. */
  iconWidth: number;
  measure: MeasureLabel;
  lineHeight: number;
  wrap: WrapLabel;
};

export type TileText = {
  lines: string[];
  /** False where the icon was given up so the text had room. */
  showIcon: boolean;
};

/**
 * What a leaf tile draws, and whether its icon survived.
 *
 * Both come out of one decision, against one width. Taking the icon from the value-first attempt
 * and the lines from the name-only one left a tile drawing a name with the icon already given up
 * for it.
 *
 * A sliver of a tile cannot afford both: at 36px wide the icon and the padding leave four pixels.
 * The text is what the tile is for, so the icon is what gets given up for it.
 */
export const toTileText = ({
  label,
  valueLabel,
  availableWidth,
  availableHeight,
  iconWidth,
  measure,
  lineHeight,
  wrap,
}: ToTileTextArgs): TileText => {
  const linesFor = (iconAllowance: number) => {
    const textWidth = availableWidth - iconAllowance;
    return rankLabelLines({
      label,
      valueLabel,
      withValue: toLeafText({
        label: label ?? '',
        valueLabel,
        availableWidth: textWidth,
        availableHeight,
        measure,
        lineHeight,
        wrap,
      }),
      nameOnly:
        label && textWidth > 0 && availableHeight > 0
          ? legibleLines(
              wrap({
                text: label,
                availableWidth: textWidth,
                measure,
                maxLines: Math.max(1, Math.floor(availableHeight / lineHeight)),
              })
            )
          : [],
    });
  };

  const withIcon = linesFor(iconWidth);
  if (withIcon.length || !iconWidth) {
    return { lines: withIcon, showIcon: iconWidth > 0 };
  }
  return { lines: linesFor(0), showIcon: false };
};
